import { formatIqd, fromMillidinars, PRINT_LOGO_WIDTH_DOTS, type ReceiptDto } from '@gc/shared';

import { serviceChargeSign } from './checkoutPreview';

/**
 * WHERE EVERY MARK ON A DRAWN RECEIPT GOES — worked out without a canvas.
 *
 * The receipt is drawn rather than typed because the printer's own font cannot
 * do hairline rules, centred cells or shaped Arabic. But canvas code is exactly
 * the kind of code this workspace cannot test: no DOM emulation ships here, so
 * anything touching `getContext` is unverifiable.
 *
 * So the arithmetic — column widths, wrapping, row heights, the total height of
 * the paper — lives HERE and is pure, and `receiptCanvas.ts` does nothing but
 * paint what this returns. Text measurement arrives as a callback, so the tests
 * inject a predictable one and assert the layout instead of the pixels.
 *
 * The MONEY IS NOT COMPUTED HERE. Every figure is formatted straight off the
 * `ReceiptDto` the server produced. This file decides where numbers sit, never
 * what they are.
 */

/** Dots across, matching the paper and the packing the logo already uses. */
export const RECEIPT_DOTS = PRINT_LOGO_WIDTH_DOTS;

/** Left and right margin, in dots. */
const MARGIN = 12;
/** Breathing room inside a table cell, each side. */
const CELL_PAD = 8;

/**
 * Sized off the owner's mockup, which is generous — its table breathes, and a
 * cramped one is what made the typed receipt read as a fallback rather than a
 * document. Every size is in printer dots at 203 dpi, so 22 dots is a hair
 * under 3mm of letter height: comfortably readable on till paper.
 */
export const FONTS = {
  title: 'bold 30px Arial, sans-serif',
  meta: '21px Arial, sans-serif',
  client: 'bold 26px Arial, sans-serif',
  head: 'bold 23px Arial, sans-serif',
  body: '23px Arial, sans-serif',
  total: 'bold 34px Arial, sans-serif',
} as const;

/** Line box for each font, in dots — what one row of that text occupies. */
const LINE_HEIGHT = {
  title: 38,
  meta: 28,
  client: 34,
  head: 30,
  body: 30,
  total: 42,
} as const;

/** Measures a string in dots for a given font. The canvas supplies the real one. */
export type Measure = (text: string, font: string) => number;

export type Align = 'left' | 'centre' | 'right';

/** The two glyphs the header carries, beside the address and the phone. */
export type Icon = 'pin' | 'phone';

export type Block =
  | { kind: 'text'; text: string; font: string; align: Align; y: number; height: number }
  /** Label on the left, money hard against the right — `Client: … Pasha`. */
  | { kind: 'pair'; left: string; right: string; font: string; y: number; height: number }
  /** Address and phone on ONE line, each behind its own icon. */
  | {
      kind: 'iconline';
      y: number;
      height: number;
      font: string;
      left: { icon: Icon; text: string };
      right: { icon: Icon; text: string } | null;
    }
  | { kind: 'gap'; y: number; height: number }
  /** `x`/`width` so a table rule can be inset to the table's own box. */
  | { kind: 'rule'; y: number; height: number; x: number; width: number }
  /** A boxed table row: cell text already wrapped, with its column geometry. */
  | {
      kind: 'row';
      y: number;
      height: number;
      header: boolean;
      /** Left edge and full width of the table, so the row can be boxed. */
      x: number;
      width: number;
      /** False for the footer strip: dividers between cells, no frame. */
      boxed: boolean;
      font: string;
      cells: Array<{ lines: string[]; x: number; width: number; align: Align }>;
    };

export interface ReceiptLayout {
  widthDots: number;
  heightDots: number;
  blocks: Block[];
}

/** One priced line, as the table shows it. Mirrors the text renderer's columns. */
interface Row {
  name: string;
  qty: string;
  price: string;
  total: string;
  after: string;
}

function money(md: string, symbol = ''): string {
  return formatIqd(fromMillidinars(md), { currencySymbol: symbol }).trim();
}

/**
 * `YYYY-MM-DD HH:MM`, in the till's own clock.
 *
 * The typed renderer converts to the venue's configured zone because it runs on
 * a server in Virginia. This runs on the till standing in the venue, so its
 * local clock IS the venue clock — converting again would be guessing at a
 * zone it already has.
 */
const pad = (value: number): string => String(value).padStart(2, '0');

/** `DD/MM/YYYY`, as the mockup's footer has it. */
export function receiptDate(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return `${pad(at.getDate())}/${pad(at.getMonth() + 1)}/${at.getFullYear()}`;
}

/** `10:45 PM` — twelve-hour, as the mockup's footer has it. */
export function receiptClock(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const hours = at.getHours();
  const twelve = hours % 12 === 0 ? 12 : hours % 12;
  return `${twelve}:${pad(at.getMinutes())} ${hours < 12 ? 'AM' : 'PM'}`;
}

/**
 * Wrap to a pixel width, breaking at spaces and hard-breaking a word that
 * cannot fit — a product name must never push a cell past its column, because
 * on paper that overlaps the number beside it and both become unreadable.
 */
export function wrapToWidth(text: string, width: number, font: string, measure: Measure): string[] {
  const out: string[] = [];
  let line = '';

  const push = (): void => {
    if (line !== '') out.push(line);
    line = '';
  };

  for (const word of text.split(' ')) {
    let rest = word;
    while (measure(rest, font) > width && rest.length > 1) {
      let cut = rest.length;
      while (cut > 1 && measure(rest.slice(0, cut), font) > width) cut -= 1;
      push();
      out.push(rest.slice(0, cut));
      rest = rest.slice(cut);
    }
    if (rest === '') continue;
    const candidate = line === '' ? rest : `${line} ${rest}`;
    if (measure(candidate, font) <= width) line = candidate;
    else {
      push();
      line = rest;
    }
  }

  push();
  return out.length > 0 ? out : [''];
}

/** The priced lines, in the order they print. */
export function receiptRows(receipt: ReceiptDto): Row[] {
  const rows: Row[] = [];

  for (const session of receipt.sessions) {
    const rounds = session.singleRounds + session.doubleRounds + session.threeBallRounds;
    const isRound = session.reservationType === 'ROUND';
    const payable = session.payableMd ?? session.totalMd;
    rows.push({
      name: session.deviceName,
      qty: isRound ? `${rounds}` : session.duration,
      price: money(isRound ? session.roundPriceMd : session.hourlyPriceMd),
      total: money(session.totalMd),
      after: money(payable),
    });
  }

  for (const order of receipt.orders) {
    for (const item of order.items) {
      const unit =
        item.quantity > 0
          ? (BigInt(item.lineTotalMd) / BigInt(item.quantity)).toString()
          : item.lineTotalMd;
      rows.push({
        name: item.name,
        qty: `${item.quantity}`,
        price: money(unit),
        total: money(item.lineTotalMd),
        // A plate carries no discount of its own; the order's does, below.
        after: money(item.lineTotalMd),
      });
    }

    // NO "Order discount" row (owner, 2026-08-15: "I want that text to be
    // entirely gone"). The order's reduction still reaches the paper — it is
    // inside `discountMd`, so the summary's Discount line and TOTAL carry it —
    // but it no longer takes a row of the items table.
  }

  return rows;
}

/** Lay the whole receipt out, to the owner's mockup. */
export function layoutReceipt(receipt: ReceiptDto, measure: Measure): ReceiptLayout {
  const rows = receiptRows(receipt);
  const symbol = receipt.currencySymbol;

  /*
   * FOUR COLUMNS: Name, Qty, Before, After — the owner's mockup exactly.
   *
   * "Before" is what the line came to, "After" what it costs once the party's
   * discounts are allocated to it, and BOTH print on every line. The unit-price
   * column that used to sit between them is gone: he drew the receipt he wants
   * and it is not there, and at this width a fifth column is paid for out of
   * the name.
   */
  const headers = ['Name', 'Qty', 'Before', 'After'];
  const pick = (row: Row): string[] => [row.name, row.qty, row.total, row.after];

  const numeric = headers
    .slice(1)
    .map((header, index) =>
      Math.max(
        measure(header, FONTS.head),
        ...rows.map((row) => measure(pick(row)[index + 1] ?? '', FONTS.body)),
      ),
    );
  const inner = RECEIPT_DOTS - MARGIN * 2;
  // Generous columns: the mockup's table breathes, and a cramped one is what
  // made the typed receipt read as a fallback rather than a document.
  const column = (width: number): number => Math.max(width, 54) + CELL_PAD * 3;
  const numericWidth = numeric.reduce((sum, width) => sum + column(width), 0);
  const nameWidth = Math.max(140, inner - numericWidth);

  const widths = [nameWidth, ...numeric.map(column)];
  const xs: number[] = [];
  let x = MARGIN;
  for (const width of widths) {
    xs.push(x);
    x += width;
  }
  const tableX = MARGIN;
  const tableWidth = widths.reduce((sum, width) => sum + width, 0);

  const blocks: Block[] = [];
  let y = 0;

  const text = (value: string, font: string, align: Align, height: number): void => {
    blocks.push({ kind: 'text', text: value, font, align, y, height });
    y += height;
  };
  const pair = (
    left: string,
    right: string,
    font: string = FONTS.body,
    height: number = LINE_HEIGHT.body,
  ): void => {
    blocks.push({ kind: 'pair', left, right, font, y, height });
    y += height;
  };
  const gap = (height: number): void => {
    blocks.push({ kind: 'gap', y, height });
    y += height;
  };
  // Three dots, not two (owner, 2026-08-15: "straight thick black lines") —
  // and four where a line is doing emphasis work. One weight family across
  // the whole paper; the ESC/POS fallback draws its bars to read the same.
  const rule = (thickness = 3, at = MARGIN, width = RECEIPT_DOTS - MARGIN * 2): void => {
    blocks.push({ kind: 'rule', y, height: thickness, x: at, width });
    y += thickness;
  };
  const tableRule = (thickness = 3): void => rule(thickness, tableX, tableWidth);

  /** One row of a table: cell text wrapped, geometry decided. */
  const rowOf = (
    cells: Array<{ text: string; x: number; width: number; align: Align }>,
    font: string,
    lineHeight: number,
    options: { header?: boolean; boxed?: boolean } = {},
  ): void => {
    const wrapped = cells.map((cell) =>
      wrapToWidth(cell.text, cell.width - CELL_PAD * 2, font, measure),
    );
    const height = Math.max(...wrapped.map((lines) => lines.length)) * lineHeight + CELL_PAD * 2;

    blocks.push({
      kind: 'row',
      y,
      height,
      header: options.header ?? false,
      x: tableX,
      width: tableWidth,
      boxed: options.boxed ?? true,
      font,
      cells: wrapped.map((lines, index) => ({
        lines,
        x: cells[index]!.x,
        width: cells[index]!.width,
        align: cells[index]!.align,
      })),
    });
    y += height;
  };

  /* ---------------------------------------------------------------- head ---
   * THE LOGO IS THE VENUE'S NAME HERE. The mockup carries no "GAMERS ESCAPE"
   * line under the picture, and the server prepends that picture above
   * everything drawn on this canvas — printing the name as text as well would
   * say it twice.
   */
  gap(6);
  if (receipt.venueAddress || receipt.venuePhone) {
    blocks.push({
      kind: 'iconline',
      y,
      height: LINE_HEIGHT.meta,
      font: FONTS.meta,
      left: { icon: 'pin', text: receipt.venueAddress ?? '' },
      right: receipt.venuePhone ? { icon: 'phone', text: receipt.venuePhone } : null,
    });
    y += LINE_HEIGHT.meta;
  }
  gap(8);
  rule(4);
  gap(8);
  pair('Client:', receipt.tabName, FONTS.client, LINE_HEIGHT.client);
  // The number the customer reads back to us (owner, 2026-09-23); Bills finds
  // every bill on the paper by it. In the client's own bold size, because it
  // is the one thing on this paper somebody will read back to us over the
  // counter. A receipt from before numbering has none.
  if (receipt.receiptNumber) {
    pair('Receipt No.', receipt.receiptNumber, FONTS.client, LINE_HEIGHT.client);
  }
  gap(10);

  /* --------------------------------------------------------------- table --- */
  tableRule();
  rowOf(
    headers.map((header, index) => ({
      text: header,
      x: xs[index]!,
      width: widths[index]!,
      align: 'centre' as Align,
    })),
    FONTS.head,
    LINE_HEIGHT.head,
    { header: true },
  );
  tableRule();
  for (const row of rows) {
    rowOf(
      pick(row).map((value, index) => ({
        text: value,
        x: xs[index]!,
        width: widths[index]!,
        // The name reads as a label and sits left; the figures are centred in
        // their cells, exactly as the mockup has them.
        align: (index === 0 ? 'left' : 'centre') as Align,
      })),
      FONTS.body,
      LINE_HEIGHT.body,
    );
    tableRule();
  }

  /* ------------------------------------------------------------- summary ---
   * Its own box under the table, split once — labels left, money right. The
   * mockup runs Price and Discount together with no line between them, then
   * fences TOTAL on its own.
   */
  gap(12);
  const labelWidth = Math.round(tableWidth * 0.45);
  const summary = (label: string, amount: string, font: string, lineHeight: number): void =>
    rowOf(
      [
        { text: label, x: tableX, width: labelWidth, align: 'left' },
        { text: amount, x: tableX + labelWidth, width: tableWidth - labelWidth, align: 'right' },
      ],
      font,
      lineHeight,
    );

  tableRule();
  summary('Price', money(receipt.subtotalMd, symbol), FONTS.body, LINE_HEIGHT.body);

  /*
   * The service charge, straight under the price it was charged on and above
   * every discount — it is added BEFORE them (owner, 2026-09-23). No row at all
   * at 0%, when the cashier removed it, or on an old payload without it: "if
   * the service is 0% it should not be mentioned in the receipt at all".
   */
  const serviceChargePercent = receipt.serviceChargePercent ?? 0;
  const serviceChargeMd = receipt.serviceChargeMd ?? '0';
  if (serviceChargePercent > 0 && serviceChargeMd !== '0') {
    summary(
      `Service charge (${serviceChargePercent}%)`,
      `${serviceChargeSign(serviceChargeMd)}${money(serviceChargeMd, symbol)}`,
      FONTS.body,
      LINE_HEIGHT.body,
    );
  }

  /*
   * A bucket prints when it carries an AMOUNT, not when it carries a percent:
   * a haggled sum has no percentage, and gating on one would hide the line and
   * let that money reappear unlabelled in the residual below. Zero prints
   * nothing — "Cafe discount 0" reads as a mistake.
   *
   * The mockup shows a single "Discount" because its example had one. A party
   * with a time AND a cafe percentage gets a line each, in the same box.
   */
  const bucket = (label: string, amountMd: string, percent: number | null): void => {
    if (amountMd === '0') return;
    summary(
      percent === null ? label : `${label} ${percent}%`,
      `-${money(amountMd, symbol)}`,
      FONTS.body,
      LINE_HEIGHT.body,
    );
  };
  bucket('Time discount', receipt.timeDiscountMd, receipt.timeDiscountPercent);
  bucket('Cafe discount', receipt.cafeDiscountMd, receipt.cafeDiscountPercent);
  bucket('Kitchen discount', receipt.kitchenDiscountMd, receipt.kitchenDiscountPercent);
  bucket('Shisha discount', receipt.shishaDiscountMd, receipt.shishaDiscountPercent);

  // Per-line discounts sit inside `discountMd` but inside none of the buckets,
  // so without this the paper's own arithmetic would not close:
  // Price − printed discounts ≠ TOTAL.
  const residual =
    BigInt(receipt.discountMd) -
    BigInt(receipt.timeDiscountMd) -
    BigInt(receipt.cafeDiscountMd) -
    BigInt(receipt.kitchenDiscountMd) -
    BigInt(receipt.shishaDiscountMd) -
    BigInt(receipt.manualDiscountMd);
  if (residual > 0n) {
    summary('Discount', `-${money(residual.toString(), symbol)}`, FONTS.body, LINE_HEIGHT.body);
  }
  if (receipt.manualDiscountMd !== '0') {
    summary(
      'Discount',
      `-${money(receipt.manualDiscountMd, symbol)}`,
      FONTS.body,
      LINE_HEIGHT.body,
    );
  }

  tableRule(4);
  summary('TOTAL', money(receipt.grandTotalMd, symbol), FONTS.total, LINE_HEIGHT.total);
  tableRule(4);

  // "Paid by FIB" (2026-09-24): money taken through a payment provider says
  // so on the paper the customer keeps. Electronic only — a cash or balance
  // receipt draws exactly what it always drew.
  if (receipt.method === 'ELECTRONIC') {
    gap(6);
    text(
      receipt.electronicProviderLabel
        ? `Paid by ${receipt.electronicProviderLabel}`
        : 'Paid electronically',
      FONTS.body,
      'centre',
      LINE_HEIGHT.body,
    );
  }

  /* -------------------------------------------------------------- footer ---
   * Cashier, date and time in one strip: divided, but not boxed.
   */
  gap(12);
  rule(4);
  gap(4);
  const third = Math.round(tableWidth / 3);
  rowOf(
    [
      { text: `Cashier: ${receipt.cashierName}`, x: tableX, width: third, align: 'left' },
      { text: receiptDate(receipt.issuedAt), x: tableX + third, width: third, align: 'centre' },
      {
        text: receiptClock(receipt.issuedAt),
        x: tableX + third * 2,
        width: tableWidth - third * 2,
        align: 'centre',
      },
    ],
    FONTS.meta,
    LINE_HEIGHT.meta,
    { boxed: false },
  );
  gap(16);

  return { widthDots: RECEIPT_DOTS, heightDots: y, blocks };
}
